import React, { useMemo } from 'react';
import {
  FileCheck,
  AlertTriangle,
  Clock,
  Shield,
  ArrowRight,
  Inbox,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { MetricCard } from '../components/shared/MetricCard';
import { QualityBadge, DocumentStatusBadge } from '../components/shared/StatusBadge';
import { useApp } from '../context/AppContext';

/* ── helpers ──────────────────────────────────────────────────── */
const CATEGORY_LABELS: Record<string, string> = {
  data_mismatch: 'Data Mismatch',
  test_requirement: 'Test Requirements',
  compliance_gap: 'Compliance Gap',
  signature_mismatch: 'Signature Issues',
  formatting: 'Formatting',
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/* ── empty state placeholder ──────────────────────────────────── */
const EmptyState: React.FC<{ message?: string }> = ({ message = 'No data available' }) => (
  <div className="flex flex-col items-center justify-center py-10 text-surface-300">
    <Inbox size={32} strokeWidth={1.5} className="mb-2" />
    <span className="text-xs font-medium">{message}</span>
  </div>
);

/* ── tooltip ──────────────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload) return null;
  return (
    <div className="glass-card px-3 py-2 text-xs shadow-lg">
      <p className="font-medium text-surface-700 mb-1">{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} className="text-surface-500">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: entry.color }} />
          {entry.name}: <span className="font-medium text-surface-700">{entry.value}</span>
        </p>
      ))}
    </div>
  );
};

/* ── main component ───────────────────────────────────────────── */
export const Dashboard: React.FC = () => {
  const { filteredDocuments, documents, navigateToDocument, setActiveView } = useApp();

  /* ── compute metrics from real data ────────────────────────── */
  const totalROVs = documents.length;
  const totalIssues = documents.reduce((sum, d) => sum + (d.issuesDetected || 0), 0);

  const completedDocs = documents.filter(d => d.status === 'completed');
  const avgProcessingTime = completedDocs.length > 0 ? '47s' : 'N/A';

  const ratedDocs = documents.filter(d => d.qualityRating);
  const qualityScore = ratedDocs.length > 0
    ? ((ratedDocs.filter(d => d.qualityRating === 'satisfactory').length / ratedDocs.length) * 100).toFixed(0)
    : null;

  /* ── quality distribution from real data ────────────────────── */
  const qualityDistribution = useMemo(() => {
    const counts = { satisfactory: 0, needs_improvement: 0, unsatisfactory: 0 };
    documents.forEach(d => {
      if (d.qualityRating === 'satisfactory') counts.satisfactory++;
      else if (d.qualityRating === 'needs_improvement') counts.needs_improvement++;
      else if (d.qualityRating === 'unsatisfactory') counts.unsatisfactory++;
    });
    return [
      { name: 'Satisfactory', value: counts.satisfactory, color: '#22c55e' },
      { name: 'Needs Improvement', value: counts.needs_improvement, color: '#f59e0b' },
      { name: 'Unsatisfactory', value: counts.unsatisfactory, color: '#ef4444' },
    ];
  }, [documents]);

  const hasQualityData = qualityDistribution.some(d => d.value > 0);

  /* ── issues by category from real data ──────────────────────── */
  const issuesByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    documents.forEach(d => {
      (d.findings || []).forEach(f => {
        const cat = f.category;
        counts[cat] = (counts[cat] || 0) + 1;
      });
    });
    const total = Object.values(counts).reduce((a, b) => a + b, 0);
    return Object.entries(counts)
      .map(([category, count]) => ({
        category: CATEGORY_LABELS[category] || category,
        count,
        percentage: total > 0 ? Math.round((count / total) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [documents]);

  /* ── weekly activity from real upload dates ─────────────────── */
  const weeklyStats = useMemo(() => {
    const now = new Date();
    const dayData: Record<string, { uploads: number; issues: number; resolved: number }> = {};
    DAY_NAMES.forEach(d => { dayData[d] = { uploads: 0, issues: 0, resolved: 0 }; });

    documents.forEach(d => {
      const uploadDay = new Date(d.uploadDate);
      const diffDays = Math.floor((now.getTime() - uploadDay.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays < 7) {
        const dayName = DAY_NAMES[uploadDay.getDay()];
        dayData[dayName].uploads++;
        dayData[dayName].issues += d.issuesDetected || 0;
        if (d.status === 'completed') dayData[dayName].resolved += d.issuesDetected || 0;
      }
    });

    // Sort starting from Monday
    const orderedDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return orderedDays.map(day => ({ day, ...dayData[day] }));
  }, [documents]);

  const hasWeeklyData = weeklyStats.some(d => d.uploads > 0);

  return (
    <div className="space-y-6">
      {/* Metrics Grid */}
      <div className="grid grid-cols-4 gap-4">
        <MetricCard
          label="Total Documents Analyzed"
          value={totalROVs > 0 ? String(totalROVs) : 'N/A'}
          icon={FileCheck}
          iconColor="text-brand-400"
          iconBg="bg-brand-50"
          className="animate-slide-up"
          onClick={() => setActiveView('documents')}
        />
        <MetricCard
          label="Issues Detected"
          value={totalROVs > 0 ? String(totalIssues) : 'N/A'}
          icon={AlertTriangle}
          iconColor="text-amber-600"
          iconBg="bg-amber-50"
          className="animate-slide-up"
          onClick={() => setActiveView('feedback')}
        />
        <MetricCard
          label="Avg Processing Time"
          value={avgProcessingTime}
          icon={Clock}
          iconColor="text-blue-600"
          iconBg="bg-blue-50"
          className="animate-slide-up"
          onClick={() => setActiveView('documents')}
        />
        <MetricCard
          label="Quality Score"
          value={qualityScore !== null ? `${qualityScore}%` : 'N/A'}
          icon={Shield}
          iconColor="text-emerald-600"
          iconBg="bg-emerald-50"
          className="animate-slide-up"
          onClick={() => setActiveView('documents')}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-3 gap-4">
        {/* Weekly Activity Chart */}
        <div
          className="col-span-2 glass-card p-5 animate-slide-up cursor-pointer hover:shadow-md hover:border-brand-200/60 transition-all"
          onClick={() => setActiveView('documents')}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="section-title">Weekly Activity</h3>
              <p className="section-subtitle">Uploads, issues detected & resolved</p>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-brand-400" />Uploads
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />Issues
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />Resolved
              </span>
            </div>
          </div>
          {hasWeeklyData ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={weeklyStats}>
                <defs>
                  <linearGradient id="gradUploads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34bebd" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#34bebd" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradIssues" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fbbf24" stopOpacity={0.1} />
                    <stop offset="100%" stopColor="#fbbf24" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="resolved" stroke="#22c55e" fill="transparent" strokeWidth={1.5} dot={false} name="Resolved" />
                <Area type="monotone" dataKey="issues" stroke="#fbbf24" fill="url(#gradIssues)" strokeWidth={1.5} dot={false} name="Issues" />
                <Area type="monotone" dataKey="uploads" stroke="#34bebd" fill="url(#gradUploads)" strokeWidth={2} dot={false} name="Uploads" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState message="No uploads this week" />
          )}
        </div>

        {/* Quality Distribution Donut */}
        <div
          className="glass-card p-5 animate-slide-up cursor-pointer hover:shadow-md hover:border-brand-200/60 transition-all"
          onClick={() => setActiveView('documents')}
        >
          <h3 className="section-title mb-1">Quality Distribution</h3>
          <p className="section-subtitle mb-4">Across all analyzed documents</p>
          {hasQualityData ? (
            <>
              <div className="flex justify-center mb-4">
                <ResponsiveContainer width={160} height={160}>
                  <PieChart>
                    <Pie
                      data={qualityDistribution.filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={3}
                      dataKey="value"
                      strokeWidth={0}
                    >
                      {qualityDistribution.filter(d => d.value > 0).map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2.5">
                {qualityDistribution.map((item) => (
                  <div key={item.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-xs text-surface-600">{item.name}</span>
                    </div>
                    <span className="text-xs font-semibold text-surface-800">{item.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <EmptyState message="No quality ratings yet" />
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-3 gap-4">
        {/* Recent Documents Table */}
        <div className="col-span-2 glass-card animate-slide-up">
          <div className="flex items-center justify-between px-5 pt-5 pb-3">
            <div>
              <h3 className="section-title">Recent Documents</h3>
              <p className="section-subtitle">Latest document submissions</p>
            </div>
            <button
              onClick={() => setActiveView('documents')}
              className="flex items-center gap-1.5 text-xs font-medium text-brand-400 hover:text-brand-500 transition-colors"
            >
              View all <ArrowRight size={13} />
            </button>
          </div>
          {filteredDocuments.length > 0 ? (
            <div className="overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="table-header">
                    <th className="table-cell">File Name</th>
                    <th className="table-cell">Document Name</th>
                    <th className="table-cell">Status</th>
                    <th className="table-cell">Quality</th>
                    <th className="table-cell text-right">Issues</th>
                    <th className="table-cell text-right">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.slice(0, 5).map((doc) => (
                    <tr
                      key={doc.id}
                      onClick={() => navigateToDocument(doc.id)}
                      className="table-row cursor-pointer"
                    >
                      <td className="px-5 py-3">
                        <span className="text-sm font-medium text-surface-800">{doc.fileName}</span>
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-xs font-mono text-brand-400">{doc.docName}</span>
                      </td>
                      <td className="px-5 py-3">
                        <DocumentStatusBadge status={doc.status} />
                      </td>
                      <td className="px-5 py-3">
                        {doc.qualityRating ? (
                          <QualityBadge quality={doc.qualityRating as any} />
                        ) : (
                          <span className="text-xs text-surface-400">N/A</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <span className="text-sm text-surface-600">{doc.issuesDetected ?? 'N/A'}</span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <span className="text-xs text-surface-500">
                          {new Date(doc.uploadDate).toLocaleDateString()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState message="No documents uploaded yet" />
          )}
        </div>

        {/* Issues by Category */}
        <div
          className="glass-card p-5 animate-slide-up cursor-pointer hover:shadow-md hover:border-brand-200/60 transition-all"
          onClick={() => setActiveView('feedback')}
        >
          <h3 className="section-title mb-1">Issues by Category</h3>
          <p className="section-subtitle mb-4">Distribution across all documents</p>
          {issuesByCategory.length > 0 ? (
            <div className="space-y-3">
              {issuesByCategory.map((item) => (
                <div key={item.category} className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-surface-600">{item.category}</span>
                    <span className="font-medium text-surface-700">{item.count} ({item.percentage}%)</span>
                  </div>
                  <div className="h-2 bg-surface-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-brand-300 to-brand-400 transition-all duration-700"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState message="No issues detected yet" />
          )}
        </div>
      </div>
    </div>
  );
};
